import axios, { InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import _ from 'lodash';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import { MdMessage } from 'mdesign3';
import router from '../router/index';
import proceessEnv from '../config/user.env';
import store from '../store';
import { authPath } from '@/api/common';
const RESPONSE_STATUS = {
  OK: 200,
  NOT_LOGIN: 401,
  NO_PERMISSON: 403,
  NOT_FOUND: 404,
  SERVER_ERROR: 500,
  MORE_ACCOUNT: '100027',
  SLIDER_CAPTCHA: '100028',
  NO_ROLE: '100011',
};
const MILLISECOND_PER_MINUTE = 60000;
const MINUTES = 30;

// 创建axios实例
const service = axios.create({
  // 在请求地址前面加上baseURL
  baseURL: proceessEnv.apiUrl,
  timeout: MILLISECOND_PER_MINUTE * MINUTES,
  withCredentials: true,
});

// 定义自定义的请求配置接口
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  showProgress?: boolean;
}

// request拦截器
service.interceptors.request.use(
  (config: CustomAxiosRequestConfig) => {
    // 使用自定义接口
    // 检查是否需要显示进度条，默认为 true
    const showProgress = config.showProgress !== false;
    if (showProgress) {
      NProgress.start();
    }
    const currentTenant = store.state.user.currentTenant || store.state.currentTenant;
    const tenantCode = currentTenant ? currentTenant.tenantCode : null;

    if (tenantCode) {
      config.headers.tenantCode = tenantCode;
    }
    // 从cookie读取token并设置到请求头
    // 跨域请求需要携带cookie时，确保withCredentials为true
    // token已通过document.cookie设置，浏览器会自动携带
    if (config.url?.indexOf(authPath) !== -1 && config.url?.includes('public')) {
      config.headers.appid = proceessEnv.appId;
      config.headers.appsecret = proceessEnv.appsecret;
    }
    // 过滤get请求的空参数（null, undefined, ''）
    config.params = _.chain(config)
      .get('params')
      .toPairs()
      .map((arr: []) => _.map(arr, _.trim))
      .filter((arr) => Array.isArray(arr) && !_.isNil(arr[1]) && arr[1] !== '')
      .fromPairs()
      .value();
    return config;
  },
  (error) => {
    // 修正 NProgress 的调用逻辑，避免NProgress.start() 和 NProgress.done() 的调用次数不一致，导致计数器不平衡，导致进度条卡住的问题。
    // NProgress.done();
    // 检查是否需要隐藏进度条
    // 注意：error 对象可能没有 config，需要检查
    // 需要将 error.config 断言为我们的自定义类型
    const showProgress = (error.config as CustomAxiosRequestConfig)?.showProgress !== false;
    if (showProgress) {
      NProgress.done();
    }
    Promise.reject(error);
  },
);

// respone拦截器
service.interceptors.response.use(
  (response: AxiosResponse) => {
    // 明确响应类型
    // 检查是否需要隐藏进度条
    // 需要将 response.config 断言为我们的自定义类型
    const showProgress = (response.config as CustomAxiosRequestConfig).showProgress !== false;
    if (showProgress) {
      NProgress.done();
    }
    if (response.data && response.data.code === RESPONSE_STATUS.NO_PERMISSON) {
      router.replace('/login');
    } else if (
      response.data &&
      response.data.code &&
      response.data.code !== '0' &&
      response.data.code !== RESPONSE_STATUS.MORE_ACCOUNT &&
      response.data.code !== RESPONSE_STATUS.SLIDER_CAPTCHA &&
      response.data.code !== RESPONSE_STATUS.OK &&
      response.data.code !== RESPONSE_STATUS.NO_ROLE
    ) {
      // 200 为了 oss 接口添加
      MdMessage.error({ message: response.data.msg });
      return response.data;
    } else if (
      // response.data.code === RESPONSE_STATUS.MORE_ACCOUNT ||
      response.data.code === RESPONSE_STATUS.SLIDER_CAPTCHA
    ) {
      MdMessage({
        message: response.data.msg,
        type: 'warning',
      });
      return response.data;
    } else {
      return response.data;
    }
  },
  (error) => {
    // 检查是否需要隐藏进度条
    // 注意：error 对象可能没有 config，需要检查
    // 需要将 error.config 断言为我们的自定义类型
    const showProgress = (error.config as CustomAxiosRequestConfig)?.showProgress !== false;
    if (showProgress) {
      NProgress.done();
    }
    const response = Object.assign({}, error.response);
    if (response) {
      switch (response.status) {
        case RESPONSE_STATUS.NOT_LOGIN:
          MdMessage.closeAll();
          MdMessage.error('当前没有登录用户或鉴权参数缺失，请重新登录！');
          router.replace('/login');
          break;
        case RESPONSE_STATUS.NO_PERMISSON:
          if (response.config.url.includes('/api-ils')) {
            // 仅控制/api-ils的403报错跳转登录
            MdMessage.closeAll();
            MdMessage.error('鉴权参数缺失，请重新登录！');
            router.replace('/login');
          }
          break;
        case RESPONSE_STATUS.NOT_FOUND:
          MdMessage.error('没找到接口信息');
          break;
        case RESPONSE_STATUS.SERVER_ERROR:
          MdMessage.error('系统异常');
          break;
        case 502:
          MdMessage.error('网关错误');
          break;
        case 503:
          MdMessage.error('服务不可用');
          break;
        case 504:
          MdMessage.error('网关超时');
          break;
        default:
          break;
      }
    }
    return Promise.reject(error);
  },
);

export default service;
